import React, { FunctionComponent, useCallback, useContext, useEffect, useLayoutEffect, useReducer } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader, Responsive } from 'rs-emd-ui-atoms';
import { AppliedFilters, CategoriesArea, FilterArea, FilterModal, ListView, TitleArea } from '.';
import { getLabel } from '../..';
import { PartsListContext } from '../../../../components/app/parts-list-context';
import { useUserDispatch } from '../../../../components/app/user-context';
import { getPLPFullScreenCookie, setPLPFullScreenCookie } from '../../helpers/user-settings.utils';
import { ISearchSort } from '../../models/plp/input/search-selector.model';
import { IPLPFacets } from '../../models/plp/plp-facets.model';
import { ConfirmationModal, MetaDataAndTagging, Pagination, RecentlyViewedComponent } from '../shared';
import { IPagination } from '../shared/page-controls/pagination.model';
import { OrderedFilter } from './filters/filters';
import { Action, ProductListPageActionType, ProductListPageProps, State } from './product-list-page-component.model';
import styles from './product-list-page-component.module.scss';

export const ProductListPage: FunctionComponent<ProductListPageProps> = (props: ProductListPageProps) => {
  let defaultPageSize: number = +(process.env.REACT_APP_PLP_DEFUALT_PAGE_SIZE ?? 20);

  let navigate = useNavigate();
  const location = useLocation();

  const isFromCLP = location.pathname.startsWith('/browse');

  const { partsListProps, setPartsListProps } = useContext(PartsListContext);
  const userDispatch = useUserDispatch();

  const [state, dispatch] = useReducer(reducer, {
    pageUI: { hideFilters: false, xScroll: 0 },
    isLoading: false,
    isFilterModalOpen: false,
    selectedNewFilters: getSelectedFilters(props.data.facetsArea),
    facetArea: props.data.facetsArea,
    categoriesArea: props.data.categoriesArea,
    productsArea: props.data.productsArea,
    summary: props.data.summary,
    preview: {},
    pagination: {
      offset: 0,
      size: defaultPageSize,
      numberOfPages: Math.ceil((props.data.summary?.totalProducts ?? 0) / defaultPageSize),
      totalProducts: props.data.summary?.totalProducts ?? 0,
    },
    showAddedToCart: false,
    partLists: {
      showConfirmation: false,
      listCreated: false,
    },
    tagging: props.data.tagging,
  });

  let onAddClick = useCallback(
    (id?: number, name?: string, desc?: string) => {
      dispatch({ type: ProductListPageActionType.AddToPartList, id, name, desc });

      //close parts lists modal
      setPartsListProps(undefined);
    },
    [setPartsListProps]
  );

  //open parts list modal with pre-selected product
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const partsListProduct = searchParams.get('partsListProduct')?.split(';');

    if (partsListProduct) {
      const currentProduct = state.productsArea?.products.find((p) => p.title.stockCode === partsListProduct[0]);

      // ensure current product is populated else partsList query is sent without stockCode
      if (currentProduct) {
        dispatch({
          type: ProductListPageActionType.SetPartsListProps,
          product: currentProduct,
          qty: Number(partsListProduct[1]),
        });

        setPartsListProps({
          products: currentProduct
            ? [
                {
                  stockCode: partsListProduct[0],
                  quantity: Number(partsListProduct[1]),
                },
              ]
            : undefined,
          onAddClick: onAddClick,
        });
      }
    }
  }, [navigate, location.search, onAddClick, setPartsListProps, state.productsArea]);

  useEffect(() => {
    if (partsListProps && partsListProps.newListId !== state.partLists.listId && state.partLists.listId !== undefined) {
      setPartsListProps({ ...partsListProps, newListId: state.partLists.listId });
    }
  }, [partsListProps, setPartsListProps, state.partLists.listId]);

  //filtering and sorting based on url hash
  useLayoutEffect(() => {
    if (props.locationHash) {
      let locationHashParts = props.locationHash
        .substring(1) // remove #
        .split(';') // split in pairs
        .map((pair) => {
          // return key/value array
          let parts = pair.split('=');
          if (parts.length === 2) return { key: parts[0], value: parts[1] };
          return undefined;
        });

      let pagination: IPagination = { ...state.pagination };
      pagination.size = +(locationHashParts.find((p) => p?.key === 'ps')?.value ?? pagination.size);
      pagination.offset = +(locationHashParts.find((p) => p?.key === 'po')?.value ?? 0);

      let filters: OrderedFilter[] = [];
      let facets = locationHashParts.find((p) => p?.key === 'af')?.value.split(',');
      facets?.forEach((f) => {
        let parts = f.split('_');
        if (parts.length === 2) filters.push({ id: parts[0], filter: { id: parts[1] } });
        else {
          var value = parts.pop() ?? '';
          var filterFlag = parts.join('_');
          if (value) filters.push({ id: '0', filter: { id: filterFlag } });
        }
      });

      let searchSort: ISearchSort | undefined = undefined;
      let sortField = locationHashParts.find((p) => p?.key === 'sf')?.value;
      let sortDirection = +(locationHashParts.find((p) => p?.key === 'sd')?.value ?? 0);
      if (sortField && (sortDirection === 0 || sortDirection === 1))
        searchSort = {
          sortField: sortField,
          direction: sortDirection,
        };

      filterProductsAndFacets(filters, pagination, searchSort);
    } else {
      getPLPStock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //set hide filters on layout & on resize
  useLayoutEffect(() => {
    dispatch({ type: ProductListPageActionType.ToggleFilters });
    window.addEventListener('resize', onPLPResize);

    return () => {
      window.removeEventListener('resize', onPLPResize);
    };

    function onPLPResize() {
      dispatch({ type: ProductListPageActionType.ToggleFilters });
    }
  }, []);

  useEffect(() => {
    dispatch({ type: ProductListPageActionType.UpdateOnNavigation });
  }, [props.data.summary]);

  useEffect(() => {
    dispatch({ type: ProductListPageActionType.UpdateOnNavigation });
  }, [props.data.summary]);

  return (
    <div className={`${styles['plp-container']}`}>
      {/* Head data */}
      <MetaDataAndTagging metaData={props.data.metaData} tagging={state.tagging} />

      <TitleArea
        breadcrumb={props.data.breadcrumb}
        title={props.data.title}
        labels={props.data.labels}
        description={props.data.description}
        summary={state.summary}
        pagination={{ ...state.pagination }}
        sort={state.sort}
        categoriesArea={state.categoriesArea}
        toggleFilerModal={() => toggleFilterModal()}
        hiddenFilters={state.pageUI.hideFilters}
        toggleFiltersVisbility={() => toggleFiltersVisibility()}
        updateProducts={(pagination, sort) => {
          getProducts(pagination, sort);
        }}
        getSRPFamilyPageUrl={getSRPFamilyPageUrl}
        plpService={props.plpService}
      />
      <div className={`${styles.content} ${!state.categoriesArea.length ? '' : styles.srp}`}>
        <div className={`${styles.sidebar} ${state.pageUI.hideFilters ? styles['hide-filters'] : ''}`}>
          {/* Facets */}
          {(props.data.facetsArea?.facets?.length ?? 0) > 0 && (
            <div className={styles['filter-area']}>
              {/* Applied Filters */}
              {((state.facetArea?.appliedFilters && state.facetArea?.appliedFilters.length > 0) ||
                (state.facetArea?.appliedFlagFilters && state.facetArea.appliedFlagFilters.length > 0)) && (
                <AppliedFilters
                  labels={props.data.labels}
                  appliedFilters={state.facetArea?.appliedFilters ?? []}
                  appliedFlagFilters={state.facetArea?.appliedFlagFilters ?? []}
                  selectedFilters={state.selectedNewFilters}
                  onChangeSelection={(selectedFilters) => filterProductsAndFacets(selectedFilters)}></AppliedFilters>
              )}
              <Responsive breakpoints={['desktop', 'lg-desktop', 'xl-desktop']}>
                {/* Filters */}
                {state.facetArea && (
                  <div className={styles['filter-container']}>
                    <FilterArea
                      labels={props.data.labels}
                      filterArea={state.preview.facetArea ?? state.facetArea}
                      selectedFilters={state.selectedNewFilters}
                      hasCategories={
                        state.categoriesArea.length > 0 &&
                        !(state.categoriesArea.length === 1 && state.categoriesArea[0].childCategories?.length === 1)
                      }
                      onChangeSelection={(selectedFilters) => filterProductsAndFacets(selectedFilters)}></FilterArea>
                  </div>
                )}
              </Responsive>
            </div>
          )}
          {/* Categories */}
          {state.categoriesArea.length > 0 &&
            !(state.categoriesArea.length === 1 && state.categoriesArea[0].childCategories?.length === 1) && (
              <Responsive breakpoints={['desktop', 'lg-desktop', 'xl-desktop']}>
                <CategoriesArea
                  categoriesArea={state.categoriesArea}
                  plpService={props.plpService}
                  getSRPFamilyPageUrl={getSRPFamilyPageUrl}></CategoriesArea>
              </Responsive>
            )}
        </div>

        <div className={styles.products}>
          {!state.isLoading && (
            <>
              <ListView
                config={props.data.config}
                labels={props.data.labels}
                productsArea={state.productsArea}
                sort={state.sort}
                stockService={props.productService}
                plpService={props.plpService}
                pricingService={props.pricingService}
                categoriesArea={state.categoriesArea}
                scrollPosition={state.pageUI.xScroll}
                locale={props.contextInfo.locale}
                productService={props.productService}
                setScrollPosition={(x) => {
                  dispatch({ type: ProductListPageActionType.SetScrollPosition, x: x });
                }}
                addToBasket={onAddToBasketClick}
                clearFilters={() => filterProductsAndFacets([])}
                updateProducts={(sort) => {
                  getProducts(undefined, sort);
                }}
                onAddToPartsList={onAddToPartsList}
              />

              <div className={styles['paging-area']}>
                <Pagination
                  labels={props.data.labels}
                  pagination={{ ...state.pagination }}
                  onPageSizeChange={(pagination) => getProducts(pagination)}
                />
              </div>
            </>
          )}

          {state.isLoading && (
            <div className={styles['loader-container']}>
              <Loader />
            </div>
          )}
        </div>
      </div>

      <RecentlyViewedComponent
        page={isFromCLP ? 'PLP' : 'SRP'}
        extraData={isFromCLP ? props.data.title : props.data.title.slice(1, -1)}
        className={`${styles['recently-viewed']} inner-page-wrapper`}
      />

      {/* Filter Modal */}
      {state.isFilterModalOpen && state.facetArea && (
        <FilterModal
          closeCallback={() => toggleFilterModal()}
          selectedFilters={state.selectedNewFilters}
          onSelectNewFilters={(selectedFilters) => previewNewFilters(selectedFilters)}
          onFilterProducts={(selectedFilters) => filterProducts(selectedFilters)}
          labels={props.data.labels}
          filterArea={state.facetArea}
          previewFilterArea={state.preview.facetArea ?? state.facetArea}
          summary={state.preview.summary}></FilterModal>
      )}

      {/* Added to Cart &  Parts list - confirmation modal */}
      {(state.showAddedToCart || state.partLists.showConfirmation) && (
        <ConfirmationModal
          labels={props.data.labels}
          confirmationModalTitle={
            state.showAddedToCart
              ? getLabel(props.data.labels, 'added_to_basket')
              : getLabel(props.data.labels, state.partLists.listCreated ? 'list_created_product_added' : 'product_added_to_list')
          }
          title={state.currentProduct?.product?.title.title}
          details={
            state.showAddedToCart && (
              <p className={`snippet ${styles.quantity}`}>
                {getLabel(props.data.labels, 'quantity_added').replace('[quantity]', (state.currentProduct?.qty ?? '').toString())}
              </p>
            )
          }
          imageUrl={state.currentProduct?.product?.title.primaryImageUrl}
          viewButtonHref={state.showAddedToCart ? '/basket' : ''}
          backButtonText='continue_shopping'
          viewButtonCallback={
            state.showAddedToCart ? undefined : () => onAddToPartsList(state.currentProduct?.product?.title.stockCode ?? '', 1, true)
          }
          viewButtonText={state.showAddedToCart ? getLabel(props.data.labels, 'view_basket') : getLabel(props.data.labels, 'view_list')}
          closeCallback={() => dispatch({ type: ProductListPageActionType.CloseConfirmationMessage })}
        />
      )}
    </div>
  );

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case ProductListPageActionType.ToggleFilterModal:
        return {
          ...state,
          isFilterModalOpen: !state.isFilterModalOpen,
          selectedNewFilters: getSelectedFilters(state.facetArea),
          preview: {},
        };
      case ProductListPageActionType.SetPartsListProps:
        return {
          ...state,
          currentProduct: {
            product: action.product,
            qty: action.qty ?? 1,
          },
          partLists: {
            ...state.partLists,
            showConfirmation: false,
            listId: !action.showSelectedList ? undefined : state.partLists.listId,
          },
        };
      case ProductListPageActionType.PreviewNewFilters:
        return {
          ...state,
          selectedNewFilters: [...action.newFilters],
          preview: { summary: action.data.summary, facetArea: action.data.facetArea },
        };
      case ProductListPageActionType.FilterProducts:
        let facetArea: IPLPFacets = state.preview.facetArea ?? {};
        facetArea.appliedFilters = action.data.facetArea?.appliedFilters;
        facetArea.appliedFlagFilters = action.data.facetArea?.appliedFlagFilters;
        return {
          ...state,
          productsArea: action.data.productsArea,
          facetArea: facetArea,
          categoriesArea: action.data.categoriesArea ? action.data.categoriesArea : state.categoriesArea,
          summary: state.preview.summary,
          isFilterModalOpen: false,
          isLoading: false,
        };
      case ProductListPageActionType.FilterProductsAndFacets: {
        let pagination = action.pagination;
        pagination.totalProducts = action.data.summary?.totalProducts ?? 0;
        pagination.numberOfPages = Math.ceil(pagination.totalProducts / pagination.size);

        if (!action.isOnPageLoad) pagination.offset = 0; // set page 0 if filtering

        document.body.scrollTo(0, 0);

        return {
          ...state,
          selectedNewFilters: [...action.newFilters],
          productsArea: action.data.productsArea,
          facetArea: action.data.facetArea,
          categoriesArea: action.data.categoriesArea ? action.data.categoriesArea : state.categoriesArea,
          summary: action.data.summary,
          isFilterModalOpen: false,
          isLoading: false,
          pagination: pagination,
          sort: action.sort,
        };
      }
      case ProductListPageActionType.UpdateProducts: {
        // scroll to top if not changing page size
        if (action.pagination.size === state.pagination.size) document.body.scrollTo(0, 0);

        let pagination = action.pagination;
        pagination.numberOfPages = Math.ceil(pagination.totalProducts / pagination.size);

        return {
          ...state,
          productsArea: action.data.productsArea,
          isLoading: false,
          pagination: pagination,
          sort: action.sort,
        };
      }
      case ProductListPageActionType.CloseConfirmationMessage: {
        return {
          ...state,
          partLists: {
            ...state.partLists,
            showConfirmation: false,
          },
          showAddedToCart: false,
        };
      }
      case ProductListPageActionType.AddedToCart: {
        return {
          ...state,
          showAddedToCart: true,
          currentProduct: {
            product: state.productsArea?.products.find((p) => p.title.stockCode === action.stockCode),
            qty: action.qty,
          },
        };
      }
      case ProductListPageActionType.IsLoading:
        return {
          ...state,
          isLoading: action.state,
        };
      case ProductListPageActionType.AddToPartList: {
        return {
          ...state,
          partLists: {
            listId: action.id,
            showConfirmation: true,
            listCreated: action.name ? true : false,
          },
        };
      }
      case ProductListPageActionType.UpdateOnNavigation:
        return {
          ...state,
          productsArea: props.data.productsArea,
          summary: props.data.summary,
          pagination: {
            offset: 0,
            size: defaultPageSize,
            numberOfPages: Math.ceil((props.data.summary?.totalProducts ?? 0) / defaultPageSize),
            totalProducts: props.data.summary?.totalProducts ?? 0,
          },
          tagging: props.data.tagging,
        };
      case ProductListPageActionType.SetPLPStock: {
        var products = state.productsArea?.products ?? [];
        if (state.productsArea?.products && action.data.products)
          products = [
            ...[...action.data.products, ...(state.productsArea?.products as any)]
              .reduce((acc, curr) => acc.set(curr.title.stockCode, { ...acc.get(curr.title.stockCode), ...curr }), new Map())
              .values(),
          ];
        return {
          ...state,
          productsArea: { ...state.productsArea, products: products },
        };
      }
      case ProductListPageActionType.ToggleFilters: {
        return {
          ...state,
          pageUI: {
            ...state.pageUI,
            hideFilters: !state.categoriesArea.length && getPLPFullScreenCookie(),
          },
        };
      }
      case ProductListPageActionType.SetScrollPosition: {
        return {
          ...state,
          pageUI: {
            ...state.pageUI,
            xScroll: action.x,
          },
        };
      }
    }
  }

  function onAddToBasketClick(stockCode: string, qty: number) {
    return props.basketService
      .addToBasket(
        {
          variables: { stockCode: stockCode, quantity: qty },
          actionType: !props.searchQuery ? 'tn_page' : 'sr_page',
        },
        userDispatch
      )
      .then((res) => {
        if (res.data?.addToBasketV2) {
          dispatch({ type: ProductListPageActionType.AddedToCart, stockCode, qty });
        }
      });
  }

  function toggleFilterModal() {
    dispatch({ type: ProductListPageActionType.ToggleFilterModal });
  }

  function previewNewFilters(selectedFilters: OrderedFilter[]) {
    props.plpService
      .getPreviewFilters({
        id: props.data.categoryId,
        searchQuery: props.searchQuery,
        selector: {
          facets: getSelectedFacets(selectedFilters),
        },
        navigate: navigate,
        location: location,
      })
      .then((res) =>
        dispatch({
          type: ProductListPageActionType.PreviewNewFilters,
          newFilters: selectedFilters,
          data: res,
        })
      );
  }

  function filterProducts(selectedFilters: OrderedFilter[]) {
    dispatch({ type: ProductListPageActionType.IsLoading, state: true });
    props.plpService
      .getFilteredProducts({
        id: props.data.categoryId,
        searchQuery: props.searchQuery,
        first: state.pagination.size,
        selector: {
          facets: getSelectedFacets(selectedFilters),
        },
        sort: state.sort,
        navigate: navigate,
        location: location,
        currentHash: props.locationHash,
      })
      .then((res) => {
        dispatch({ type: ProductListPageActionType.FilterProducts, data: res });
        getPLPStock(selectedFilters, state.pagination, state.sort);
      });
  }

  function filterProductsAndFacets(selectedFilters?: OrderedFilter[], pagination?: IPagination, sort?: ISearchSort) {
    let filters = selectedFilters ?? state.selectedNewFilters;
    let currPagination = pagination ?? state.pagination;
    let currSort = sort ?? state.sort;
    let isOnPageLoad = pagination ? true : false;

    dispatch({ type: ProductListPageActionType.IsLoading, state: true });

    props.plpService
      .getFilteredProductsAndFacets({
        id: props.data.categoryId,
        searchQuery: props.searchQuery,
        first: currPagination.size,
        offset: pagination ? currPagination.offset : 0, // is pagination populated? or are we filtering?
        selector: {
          facets: getSelectedFacets(filters),
        },
        sort: currSort,
        navigate: navigate,
        location: location,
        currentHash: props.locationHash,
      })
      .then((res) => {
        dispatch({
          type: ProductListPageActionType.FilterProductsAndFacets,
          newFilters: filters,
          pagination: currPagination,
          sort: currSort,
          data: res,
          isOnPageLoad,
        });

        getPLPStock(filters, currPagination, currSort);
      });
  }

  function getPLPStock(filters: OrderedFilter[] = [], pagination?: IPagination, sort?: ISearchSort) {
    if (!props.contextInfo.isBot && props.data.config.queryStock) {
      props.plpService
        .getPLPStock({
          id: props.data.categoryId,
          searchQuery: props.searchQuery,
          first: pagination?.size,
          offset: pagination?.offset ?? 0, // is pagination populated? or are we filtering?
          selector: {
            facets: getSelectedFacets(filters),
          },
          sort: sort,
          navigate: navigate,
          location: location,
        })
        .then((res) =>
          dispatch({
            type: ProductListPageActionType.SetPLPStock,
            data: res,
          })
        );
    }
  }

  function getProducts(pagination?: IPagination, sort?: ISearchSort) {
    dispatch({ type: ProductListPageActionType.IsLoading, state: true });

    let currPagination = pagination ?? state.pagination;
    sort = sort ?? state.sort;
    let filters = state.selectedNewFilters;

    if (sort !== state.sort) currPagination.offset = 0;

    props.plpService
      .getProducts({
        id: props.data.categoryId,
        searchQuery: props.searchQuery,
        first: currPagination.size,
        offset: currPagination.offset,
        selector: {
          facets: getSelectedFacets(filters),
        },
        sort: sort ?? state.sort,
        navigate: navigate,
        location: location,
        currentHash: props.locationHash,
      })
      .then((res) => {
        dispatch({ type: ProductListPageActionType.UpdateProducts, data: res, pagination: currPagination, sort: sort });
        getPLPStock(filters, currPagination, sort);
      });
  }

  function getSelectedFacets(selectedFilters: OrderedFilter[]) {
    return selectedFilters.map((f) => {
      if (f.id !== '0') return { facetId: f.id, facetValueId: f.filter.id };
      else return { facetId: f.filter.id, facetValueId: 'true' }; // flag facets
    });
  }

  function getSelectedFilters(facetsArea: IPLPFacets | null | undefined): OrderedFilter[] {
    let filters: OrderedFilter[][] =
      facetsArea?.appliedFilters?.map((f) => {
        return f.values.map((v) => {
          return {
            id: f.id,
            name: f.displayName,
            filter: v,
          };
        });
      }) ?? [];

    let flagFilters: OrderedFilter[] =
      facetsArea?.appliedFlagFilters?.map((f) => {
        return {
          id: '0',
          name: '',
          filter: f,
        };
      }) ?? [];

    return flagFilters.concat(...filters);
  }

  function onAddToPartsList(stockCode: string, qty: number, showSelectedList?: boolean) {
    const currentProduct = state.productsArea?.products.find((p) => p.title.stockCode === stockCode);
    dispatch({
      type: ProductListPageActionType.SetPartsListProps,
      product: currentProduct,
      qty,
      showSelectedList: showSelectedList,
    });

    setPartsListProps({
      products: currentProduct ? [{ stockCode: stockCode ?? '', quantity: qty }] : undefined,
      newListId: showSelectedList ? state.partLists.listId : undefined,
      onAddClick: onAddClick,
    });
  }

  function getSRPFamilyPageUrl(familyId?: string | number) {
    const queryStringParts = location.search.replace('?', '').split('&');
    const queryString = queryStringParts.filter((q) => q.startsWith('query=') || q.startsWith('tag='));
    const familyQuery = familyId ? '&familyId=' + familyId : '';

    const urlHashParts = location.hash.replace('#', '').split(';');
    const facets = urlHashParts.filter((h) => h.startsWith('af='));

    return `${location.pathname}?${queryString.join('&')}${familyQuery}#${facets}`;
  }

  function toggleFiltersVisibility() {
    setPLPFullScreenCookie(!state.pageUI.hideFilters);
    dispatch({ type: ProductListPageActionType.ToggleFilters });
  }
};
